import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { CustomInput } from "../../../components-new/common";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSaveApiEndpoints } from "../../../useQuery";
import { useNotification } from "../../../hooks";

const FAILURE_ACTIONS = [
  { value: "fail_step", label: "Fail Step" },
  { value: "notify", label: "Send Notification" },
  { value: "both", label: "Both" },
];

const NOTIFICATION_PROVIDERS = [
  {
    value: "slack",
    label: "Slack",
    webhookPlaceholder: "https://hooks.slack.com/services/...",
    webhookHelpText: "Enter Slack webhook URL for notifications",
  },
  {
    value: "teams",
    label: "Microsoft Teams",
    webhookPlaceholder: "https://outlook.office.com/webhook/...",
    webhookHelpText: "Enter Microsoft Teams webhook URL for notifications",
  },
  {
    value: "azure",
    label: "Azure DevOps",
    webhookPlaceholder: "https://dev.azure.com/...",
    webhookHelpText: "Enter Azure DevOps webhook URL for notifications",
  },
  {
    value: "email",
    label: "Email",
    webhookPlaceholder: "recipient@example.com",
    webhookHelpText: "Enter email address for notifications",
  },
  {
    value: "custom",
    label: "Custom Webhook",
    webhookPlaceholder: "https://your-webhook-url.com/...",
    webhookHelpText: "Enter custom webhook URL for notifications",
  },
];

const ApiEndpoints = ({ item }) => {
  const [endpoints, setEndpoints] = useState(item?.apiData?.endpoints || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });

  const [newEndpoint, setNewEndpoint] = useState("");
  const [bulkEndpoints, setBulkEndpoints] = useState("");
  const [addMode, setAddMode] = useState("single");
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [editingEndpointIndex, setEditingEndpointIndex] = useState(null);
  const notify = useNotification();

  const { mutate: saveEndpoints, isLoading } = useSaveApiEndpoints();

 
  useEffect(() => {
    const originalEndpoints = item?.apiData?.endpoints || [];
    setHasUnsavedChanges(JSON.stringify(originalEndpoints) !== JSON.stringify(endpoints));
  }, [endpoints, item?.apiData?.endpoints]);

  const handleAddEndpoint = () => {
    if (newEndpoint.trim()) {
      const newEndpointObj = {
        url: newEndpoint.trim(),
        retryConfig: {
          enabled: false,
          retryCount: 0,
          waitTime: 10,
          statusCodes: [],
        },
        failureConfig: {
          action: "fail_step",
          notifications: [],
        },
      };

      setEndpoints([...endpoints, newEndpointObj]);
      setNewEndpoint("");
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkEndpoints.split("\n").filter((line) => line.trim());
    const newEndpoints = lines.map((line) => ({
      url: line.trim(),
      retryConfig: {
        enabled: false,
        retryCount: 0,
        waitTime: 10,
        statusCodes: [],
      },
      failureConfig: {
        action: "fail_step",
        notifications: [],
      },
    }));

    setEndpoints([...endpoints, ...newEndpoints]);
    setBulkEndpoints("");
  };

  const handleDeleteEndpoint = (index) => {
    setDeleteConfirmDialog({ open: true, index });
  };

  const confirmDeleteEndpoint = () => {
    const updatedEndpoints = [...endpoints];
    updatedEndpoints.splice(deleteConfirmDialog.index, 1);
    setEndpoints(updatedEndpoints);
    setDeleteConfirmDialog({ open: false, index: null });
    notify("Endpoint deleted. Click 'Save Endpoints' to apply changes.", "info");
  };

  const handleEditEndpoint = (endpoint, index) => {
    setEditingEndpoint(endpoint);
    setEditingEndpointIndex(index);
  };

  const handleSaveEndpointConfig = (updatedEndpoint) => {
    const updatedEndpoints = [...endpoints];
    updatedEndpoints[editingEndpointIndex] = updatedEndpoint;
    setEndpoints(updatedEndpoints);
    setEditingEndpoint(null);
    setEditingEndpointIndex(null);
  };

  const handleSave = () => {
    if (!item?.idtest_step || !item?.Test_Case_Id) {
      notify("Missing test step or test case information", "error");
      return;
    }

    saveEndpoints(
      {
        testCaseId: item.Test_Case_Id,
        testStepId: item.idtest_step,
        endpoints: endpoints,
      },
      {
        onSuccess: () => {
          notify("API endpoints saved successfully", "success");
          setHasUnsavedChanges(false);
        },
        onError: (error) => {
          notify(`Error saving API endpoints: ${error.message}`, "error");
        },
      },
    );
  };

  const handleAddModeChange = (event, newMode) => {
    if (newMode !== null) {
      setAddMode(newMode);
    }
  };

  const EndpointConfigDialog = ({ endpoint, onSave, onClose }) => {
    const [config, setConfig] = useState(endpoint);
    const [newNotification, setNewNotification] = useState({
      provider: "",
      webhookUrl: "",
      enabled: true,
    });
    const [statusCodeInput, setStatusCodeInput] = useState(
      endpoint.retryConfig.statusCodes.join(", "),
    );
    const [retryCountInput, setRetryCountInput] = useState(
      endpoint.retryConfig.retryCount.toString(),
    );

    const handleRetryConfigChange = (field, value) => {
      setConfig((prev) => ({
        ...prev,
        retryConfig: {
          ...prev.retryConfig,
          [field]: value,
        },
      }));
    };

    const handleFailureConfigChange = (field, value) => {
      setConfig((prev) => ({
        ...prev,
        failureConfig: {
          ...prev.failureConfig,
          [field]: value,
        },
      }));
    };

    const handleAddNotification = () => {
      if (newNotification.provider && newNotification.webhookUrl) {
        setConfig((prev) => ({
          ...prev,
          failureConfig: {
            ...prev.failureConfig,
            notifications: [...prev.failureConfig.notifications, { ...newNotification }],
          },
        }));
        setNewNotification({
          provider: "",
          webhookUrl: "",
          enabled: true,
        });
      }
    };

    const handleRemoveNotification = (index) => {
      setConfig((prev) => ({
        ...prev,
        failureConfig: {
          ...prev.failureConfig,
          notifications: prev.failureConfig.notifications.filter((_, i) => i !== index),
        },
      }));
    };

    const handleNotificationChange = (index, field, value) => {
      setConfig((prev) => ({
        ...prev,
        failureConfig: {
          ...prev.failureConfig,
          notifications: prev.failureConfig.notifications.map((notification, i) =>
            i === index ? { ...notification, [field]: value } : notification,
          ),
        },
      }));
    };

    const handleStatusCodeInputChange = (event) => {
      const value = event.target.value;
      setStatusCodeInput(value);

      const statusCodes = value
        .split(",")
        .map((code) => code.trim())
        .filter((code) => /^\d{3}$/.test(code));

      if (statusCodes.length > 0 || value === "") {
        handleRetryConfigChange("statusCodes", statusCodes);
      }
    };

    const handleStatusCodeInputBlur = () => {
      const formattedCodes = config.retryConfig.statusCodes.join(", ");
      setStatusCodeInput(formattedCodes);
    };

    const handleRetryCountChange = (event) => {
      const value = event.target.value;
      setRetryCountInput(value);

      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 10) {
        handleRetryConfigChange("retryCount", numValue);
      } else if (value === "") {
        handleRetryConfigChange("retryCount", 0);
      }
    };

    const handleRetryCountBlur = () => {
      const numValue = parseInt(retryCountInput);
      if (isNaN(numValue) || numValue < 0) {
        setRetryCountInput("0");
        handleRetryConfigChange("retryCount", 0);
      } else if (numValue > 10) {
        setRetryCountInput("10");
        handleRetryConfigChange("retryCount", 10);
      } else {
        setRetryCountInput(numValue.toString());
      }
    };

    return (
      <Box sx={{ p: 2, bgcolor: "white", borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Configure Endpoint: {endpoint.url}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Retry Configuration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.retryConfig.enabled}
                onChange={(e) => handleRetryConfigChange("enabled", e.target.checked)}
              />
            }
            label="Enable Retry"
          />

          {config.retryConfig.enabled && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Number of Retries"
                value={retryCountInput}
                onChange={handleRetryCountChange}
                onBlur={handleRetryCountBlur}
                placeholder="Enter number of retries"
                size="small"
                helperText="Enter number of retry attempts (0-10)"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2550D1",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.75rem",
                    color: "#666",
                  },
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />

              <TextField
                label="Wait Time (seconds)"
                type="number"
                value={config.retryConfig.waitTime}
                onChange={(e) => {
                  const value = Math.max(1, Math.min(300, parseInt(e.target.value) || 10));
                  handleRetryConfigChange("waitTime", value);
                }}
                inputProps={{
                  min: 1,
                  max: 300,
                  step: 1,
                }}
                size="small"
                helperText="Enter wait time between retries (1-300 seconds)"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2550D1",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.75rem",
                    color: "#666",
                  },
                  "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                    {
                      opacity: 1,
                    },
                }}
              />

              <TextField
                label="Status Codes to Retry"
                value={statusCodeInput}
                onChange={handleStatusCodeInputChange}
                onBlur={handleStatusCodeInputBlur}
                placeholder="e.g., 500, 503, 504"
                fullWidth
                size="small"
                helperText={
                  <Box component="span" sx={{ fontSize: "0.75rem", color: "#666" }}>
                    Enter HTTP status codes separated by commas (e.g., 500, 503, 504)
                    {config.retryConfig.statusCodes.length > 0 && (
                      <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                        Current codes: {config.retryConfig.statusCodes.join(", ")}
                      </Box>
                    )}
                  </Box>
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2550D1",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    margin: "4px 0 0 0",
                  },
                }}
                inputProps={{
                  pattern: "[0-9, ]*",
                  inputMode: "numeric",
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Failure Configuration
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Failure Action</InputLabel>
            <Select
              value={config.failureConfig.action}
              onChange={(e) => handleFailureConfigChange("action", e.target.value)}
              label="Failure Action"
            >
              {FAILURE_ACTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(config.failureConfig.action === "notify" || config.failureConfig.action === "both") && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: "#404040", fontWeight: 500 }}
              >
                Notification Settings
              </Typography>

              {config.failureConfig.notifications.map((notification, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid #E2E6F3",
                    borderRadius: 1,
                    bgcolor: "#f8f9fa",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography
                      sx={{
                        color: "#404040",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      }}
                    >
                      {NOTIFICATION_PROVIDERS.find((p) => p.value === notification.provider)?.label}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={notification.enabled}
                            onChange={(e) =>
                              handleNotificationChange(index, "enabled", e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                            }}
                          >
                            Enabled
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveNotification(index)}
                        color="error"
                        sx={{
                          "&:hover": {
                            bgcolor: "rgba(211, 47, 47, 0.04)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={notification.webhookUrl}
                    onChange={(e) => handleNotificationChange(index, "webhookUrl", e.target.value)}
                    placeholder={
                      NOTIFICATION_PROVIDERS.find((p) => p.value === notification.provider)
                        ?.webhookPlaceholder
                    }
                    helperText={
                      NOTIFICATION_PROVIDERS.find((p) => p.value === notification.provider)
                        ?.webhookHelpText
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2550D1",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: "0.75rem",
                        color: "#666",
                      },
                    }}
                  />
                </Box>
              ))}

              <Box
                sx={{
                  p: 2,
                  border: "1px dashed #E2E6F3",
                  borderRadius: 1,
                  bgcolor: "#fff",
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    color: "#404040",
                    fontWeight: 500,
                    mb: 2,
                  }}
                >
                  Add New Notification
                </Typography>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel
                      sx={{
                        color: "#666",
                        "&.Mui-focused": {
                          color: "#2550D1",
                        },
                      }}
                    >
                      Provider
                    </InputLabel>
                    <Select
                      value={newNotification.provider}
                      onChange={(e) =>
                        setNewNotification((prev) => ({ ...prev, provider: e.target.value }))
                      }
                      label="Provider"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E2E6F3",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2550D1",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2550D1",
                        },
                        "& .MuiSelect-select": {
                          color: "#404040",
                        },
                      }}
                    >
                      {NOTIFICATION_PROVIDERS.map((provider) => (
                        <MenuItem
                          key={provider.value}
                          value={provider.value}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#404040",
                            "&:hover": {
                              bgcolor: "rgba(37, 80, 209, 0.04)",
                            },
                            "&.Mui-selected": {
                              bgcolor: "rgba(37, 80, 209, 0.08)",
                              "&:hover": {
                                bgcolor: "rgba(37, 80, 209, 0.12)",
                              },
                            },
                          }}
                        >
                          {provider.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    value={newNotification.webhookUrl}
                    onChange={(e) =>
                      setNewNotification((prev) => ({ ...prev, webhookUrl: e.target.value }))
                    }
                    placeholder={
                      NOTIFICATION_PROVIDERS.find((p) => p.value === newNotification.provider)
                        ?.webhookPlaceholder
                    }
                    helperText={
                      NOTIFICATION_PROVIDERS.find((p) => p.value === newNotification.provider)
                        ?.webhookHelpText
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2550D1",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: "0.75rem",
                        color: "#666",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddNotification}
                    disabled={!newNotification.provider || !newNotification.webhookUrl}
                    sx={{
                      bgcolor: "#ff5336",
                      "&:hover": {
                        bgcolor: "#e94a2f",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(0, 0, 0, 0.12)",
                        color: "rgba(0, 0, 0, 0.26)",
                      },
                      minWidth: "100px",
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(config)}
            sx={{ bgcolor: "#ff5336", "&:hover": { bgcolor: "#e94a2f" } }}
          >
            Save Configuration
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box pr={3} display="flex" flexDirection="column" height="calc(100vh - 300px)">
      {hasUnsavedChanges && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          }
        >
          You have unsaved changes. Click "Save Endpoints" to apply them.
        </Alert>
      )}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">API Endpoints</Typography>
          <Tooltip
            title="Add API endpoints that need to be completed before proceeding to the next test step. Configure retry and failure handling for each endpoint."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ color: "#8C9EB8" }}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ToggleButtonGroup value={addMode} exclusive onChange={handleAddModeChange} size="small">
          <ToggleButton value="single">
            <AddCircleOutlineIcon sx={{ mr: 1 }} />
            Single
          </ToggleButton>
          <ToggleButton value="bulk">
            <FormatListBulletedIcon sx={{ mr: 1 }} />
            Bulk
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mb={3}
        p={2}
        bgcolor="#f5f5f5"
        borderRadius={1}
      >
        {addMode === "single" ? (
          <>
            <CustomInput
              labelinput="API Endpoint URL"
              placeholder="Enter API endpoint URL"
              value={newEndpoint}
              onChange={(e) => setNewEndpoint(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddEndpoint}
              sx={{
                alignSelf: "flex-start",
                bgcolor: "#ff5336",
                "&:hover": {
                  bgcolor: "#e94a2f",
                },
              }}
            >
              Add Endpoint
            </Button>
          </>
        ) : (
          <>
            <TextField
              multiline
              rows={4}
              placeholder="Enter endpoints (one per line)&#10;Example:&#10;https://api.example.com/endpoint1&#10;https://api.example.com/endpoint2"
              value={bulkEndpoints}
              onChange={(e) => setBulkEndpoints(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Enter one endpoint URL per line
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleBulkAdd}
              sx={{
                alignSelf: "flex-start",
                bgcolor: "#ff5336",
                "&:hover": {
                  bgcolor: "#e94a2f",
                },
              }}
            >
              Add All Endpoints
            </Button>
          </>
        )}
      </Box>

      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        {endpoints.map((endpoint, index) => (
          <Box
            key={index}
            p={2}
            border="1px solid #E2E6F3"
            borderRadius={1}
            display="flex"
            flexDirection="column"
            gap={1}
            bgcolor="white"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={500}>
                {endpoint.url}
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleEditEndpoint(endpoint, index)}
                >
                  Configure
                </Button>
                <IconButton onClick={() => handleDeleteEndpoint(index)} color="error">
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Box>

            {endpoint.retryConfig.enabled && (
              <Box sx={{ pl: 2, borderLeft: "2px solid #E2E6F3" }}>
                <Typography variant="caption" color="text.secondary">
                  Retry: {endpoint.retryConfig.retryCount}{" "}
                  {endpoint.retryConfig.retryCount === 1 ? "time" : "times"}
                  {endpoint.retryConfig.statusCodes.length > 0 &&
                    ` (Status Codes: ${endpoint.retryConfig.statusCodes.join(", ")})`}
                </Typography>
              </Box>
            )}

            {endpoint.failureConfig.action !== "fail_step" && (
              <Box sx={{ pl: 2, borderLeft: "2px solid #E2E6F3" }}>
                <Typography variant="caption" color="text.secondary">
                  On Failure:{" "}
                  {endpoint.failureConfig.action === "notify"
                    ? "Send Notifications"
                    : endpoint.failureConfig.action === "both"
                      ? "Fail Step & Send Notifications"
                      : "Fail Step"}
                  {endpoint.failureConfig.notifications.length > 0 &&
                    ` (${endpoint.failureConfig.notifications.length} notification${endpoint.failureConfig.notifications.length > 1 ? "s" : ""} configured)`}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {editingEndpoint !== null && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "auto",
            zIndex: 1000,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
          }}
        >
          <EndpointConfigDialog
            endpoint={editingEndpoint}
            onSave={handleSaveEndpointConfig}
            onClose={() => {
              setEditingEndpoint(null);
              setEditingEndpointIndex(null);
            }}
          />
        </Box>
      )}

      <Box
        mt={2}
        pt={2}
        borderTop="1px solid #E2E6F3"
        bgcolor="white"
        position="sticky"
        bottom={0}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {hasUnsavedChanges && (
          <Typography color="text.secondary" variant="body2">
            You have unsaved changes
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || !hasUnsavedChanges}
          sx={{
            bgcolor: "#ff5336",
            "&:hover": {
              bgcolor: "#e94a2f",
            },
            minWidth: "150px",
          }}
        >
          {isLoading ? "Saving..." : "Save Endpoints"}
        </Button>
      </Box>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>Delete Endpoint</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this endpoint? This action cannot be undone.
            You will need to click "Save Endpoints" to apply this change.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteEndpoint} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiEndpoints;
